import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Captura el contenedor de la gráfica «Historial por célula» (Recharts).
 * @param {React.RefObject<HTMLElement|null>} chartRef
 * @returns {Promise<string|null>}
 */
async function waitForChartPaint(chartRef, maxMs = 2000) {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const el = chartRef?.current;
    if (el?.isConnected && el.querySelector("svg, .recharts-wrapper")) {
      return el;
    }
    await sleep(80);
  }
  const el = chartRef?.current;
  return el?.isConnected ? el : null;
}

export async function capturePerCellHistoryChartPng(chartRef) {
  const el = await waitForChartPaint(chartRef);
  if (!el) return null;

  el.scrollIntoView({ block: "nearest", behavior: "instant" });
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
  await sleep(550);

  try {
    const canvas = await html2canvas(el, {
      scale: Math.min(2, window.devicePixelRatio || 2),
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: el.scrollWidth,
      windowHeight: el.scrollHeight,
    });
    return canvas.toDataURL("image/png", 1.0);
  } catch {
    return null;
  }
}

function formatGrowthPct(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  return `${n > 0 ? "+" : ""}${n}%`;
}

/**
 * PDF cliente: metadatos + imagen de la gráfica visible.
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function exportPerCellHistoryChartPdf({
  chartRef,
  chartData,
  selectionLabel,
  dateFrom,
  dateTo,
  growthPct,
  averageTotal,
  sumFirstMonthGuests,
  chartBusy = false,
}) {
  const dataParaExportar = Array.isArray(chartData) ? chartData : [];
  if (chartBusy || dataParaExportar.length === 0) {
    return { ok: false, message: "No hay datos para exportar esta gráfica." };
  }

  if (!chartRef?.current?.isConnected) {
    return { ok: false, message: "No hay datos para exportar esta gráfica." };
  }

  const png = await capturePerCellHistoryChartPng(chartRef);
  if (!png) {
    return {
      ok: false,
      message: "No se pudo capturar la gráfica. Intente de nuevo en unos segundos.",
    };
  }

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 16;
  const maxW = pageW - 2 * margin;
  let y = 18;

  const line = (text, opts = {}) => {
    const size = opts.size ?? 11;
    const style = opts.bold ? "bold" : "normal";
    pdf.setFont("helvetica", style);
    pdf.setFontSize(size);
    pdf.setTextColor(30, 41, 59);
    const lines = pdf.splitTextToSize(String(text), maxW);
    pdf.text(lines, margin, y);
    y += lines.length * (size * 0.42) + (opts.gap ?? 3);
  };

  line("Historial por célula", { size: 16, bold: true, gap: 6 });
  line(`Selección: ${selectionLabel || "—"}`, { gap: 2 });
  line(`Rango de fechas: ${dateFrom || "—"} — ${dateTo || "—"}`, { gap: 2 });
  line(`Crecimiento del periodo: ${formatGrowthPct(growthPct)}`, { gap: 2 });
  line(`Promedio: ${averageTotal ?? "—"}`, { gap: 2 });
  line(`Invitados 1.er mes: ${sumFirstMonthGuests ?? 0}`, { gap: 8 });

  const pageH = pdf.internal.pageSize.getHeight();
  const imgProps = pdf.getImageProperties(png);
  let imgW = maxW;
  let imgH = (imgProps.height * imgW) / imgProps.width;
  const maxImgH = pageH - y - margin;
  if (imgH > maxImgH) {
    const s = maxImgH / imgH;
    imgH = maxImgH;
    imgW *= s;
  }

  pdf.addImage(png, "PNG", margin, y, imgW, imgH, undefined, "FAST");

  const safeLabel = (selectionLabel || "grafica")
    .replace(/[^\w\-áéíóúñÁÉÍÓÚÑ]+/gi, "_")
    .slice(0, 48);
  const safeFrom = (dateFrom || "").replace(/[^\d-]/g, "");
  const safeTo = (dateTo || "").replace(/[^\d-]/g, "");
  pdf.save(`historial_celula_${safeLabel}_${safeFrom}_${safeTo}.pdf`);

  return { ok: true };
}
